"use strict";
import { useRecentConnectorId } from "components/Web3Provider/constants";
import { useConnect } from "hooks/useConnect";
import { useCallback, useMemo } from "react";
import { CONNECTION_PROVIDER_IDS } from "uniswap/src/constants/web3";
import { FeatureFlags } from "uniswap/src/features/gating/flags";
import { useFeatureFlag } from "uniswap/src/features/gating/hooks";
import { isPlaywrightEnv } from "utilities/src/environment/env";
import { isMobileWeb } from "utilities/src/platform";
const SHOULD_THROW = { shouldThrow: true };
function getConnectorWithId(connectors, id, options) {
  const connector = connectors.find((c) => c.id === id);
  if (!connector && options?.shouldThrow) {
    throw new Error(`Expected connector ${id} missing from wagmi context.`);
  }
  return connector;
}
export function useConnectorWithId(id, options) {
  const { connectors } = useConnect();
  return useMemo(
    () => options?.shouldThrow ? getConnectorWithId(connectors, id, options) : getConnectorWithId(connectors, id),
    [connectors, id, options]
  );
}
function getInjectedConnectors(connectors, isEmbeddedWalletEnabled) {
  let isCoinbaseWalletBrowser = false;
  const injectedConnectors = connectors.filter((c) => {
    if (c.id === CONNECTION_PROVIDER_IDS.COINBASE_RDNS) {
      if (isMobileWeb) {
        isCoinbaseWalletBrowser = true;
      }
      return false;
    }
    if (c.id === CONNECTION_PROVIDER_IDS.UNISWAP_EXTENSION_RDNS && !isEmbeddedWalletEnabled) {
      return false;
    }
    return c.type === CONNECTION_PROVIDER_IDS.INJECTED_CONNECTOR_TYPE && c.id !== CONNECTION_PROVIDER_IDS.INJECTED_CONNECTOR_ID;
  });
  const fallbackInjector = getConnectorWithId(connectors, CONNECTION_PROVIDER_IDS.INJECTED_CONNECTOR_ID, {
    shouldThrow: true
  });
  if (!injectedConnectors.length && Boolean(window.ethereum)) {
    return { injectedConnectors: [fallbackInjector], isCoinbaseWalletBrowser };
  }
  return { injectedConnectors, isCoinbaseWalletBrowser };
}
export function useOrderedConnections(options) {
  const showSecondaryConnectors = options?.showSecondaryConnectors ?? false;
  const { connectors } = useConnect();
  const recentConnectorId = useRecentConnectorId();
  const isEmbeddedWalletEnabled = useFeatureFlag(FeatureFlags.EmbeddedWallet);
  const sortByRecent = useCallback(
    (a, b) => {
      if (a.id === recentConnectorId) {
        return -1;
      } else if (b.id === recentConnectorId) {
        return 1;
      } else {
        return 0;
      }
    },
    [recentConnectorId]
  );
  return useMemo(() => {
    const { injectedConnectors: injectedConnectorsBase, isCoinbaseWalletBrowser } = getInjectedConnectors(
      connectors,
      isEmbeddedWalletEnabled
    );
    const injectedConnectors = injectedConnectorsBase.map((c) => ({ ...c, isInjected: true }));
    const embeddedWalletConnector = getConnectorWithId(
      connectors,
      CONNECTION_PROVIDER_IDS.EMBEDDED_WALLET_CONNECTOR_ID,
      SHOULD_THROW
    );
    const coinbaseSdkConnector = getConnectorWithId(
      connectors,
      CONNECTION_PROVIDER_IDS.COINBASE_SDK_CONNECTOR_ID,
      SHOULD_THROW
    );
    const walletConnectConnector = getConnectorWithId(
      connectors,
      CONNECTION_PROVIDER_IDS.WALLET_CONNECT_CONNECTOR_ID,
      SHOULD_THROW
    );
    if (!coinbaseSdkConnector || !walletConnectConnector) {
      throw new Error("Expected connector(s) missing from wagmi context.");
    }
    if (isPlaywrightEnv()) {
      const mockConnector = getConnectorWithId(connectors, CONNECTION_PROVIDER_IDS.MOCK_CONNECTOR_ID, SHOULD_THROW);
      return [mockConnector];
    }
    if (isMobileWeb && injectedConnectors.length === 1) {
      return injectedConnectors;
    }
    if (isCoinbaseWalletBrowser) {
      return [coinbaseSdkConnector];
    }
    const orderedConnectors = [];
    if (showSecondaryConnectors) {
      if (isMobileWeb && isEmbeddedWalletEnabled) {
        orderedConnectors.push(embeddedWalletConnector);
      }
      const secondaryConnectors = [walletConnectConnector, coinbaseSdkConnector];
      orderedConnectors.push(...secondaryConnectors.filter((c) => c.id !== recentConnectorId || isMobileWeb));
    } else {
      orderedConnectors.push(...injectedConnectors);
      if (isEmbeddedWalletEnabled) {
        orderedConnectors.push(embeddedWalletConnector);
        if (recentConnectorId === CONNECTION_PROVIDER_IDS.COINBASE_SDK_CONNECTOR_ID) {
          orderedConnectors.push(coinbaseSdkConnector);
        } else if (recentConnectorId === CONNECTION_PROVIDER_IDS.WALLET_CONNECT_CONNECTOR_ID) {
          orderedConnectors.push(walletConnectConnector);
        }
      } else {
        orderedConnectors.push(walletConnectConnector);
        orderedConnectors.push(coinbaseSdkConnector);
      }
    }
    orderedConnectors.sort(sortByRecent);
    return orderedConnectors;
  }, [connectors, isEmbeddedWalletEnabled, recentConnectorId, showSecondaryConnectors, sortByRecent]);
}
export var ExtensionRequestMethods = /* @__PURE__ */ ((ExtensionRequestMethods2) => {
  ExtensionRequestMethods2["OPEN_SIDEBAR"] = "uniswap_openSidebar";
  return ExtensionRequestMethods2;
})(ExtensionRequestMethods || {});
const ExtensionRequestArguments = {
  ["uniswap_openSidebar" /* OPEN_SIDEBAR */]: ["Tokens", "Activity"]
};
export function useUniswapExtensionConnector() {
  const connector = useConnectorWithId(CONNECTION_PROVIDER_IDS.UNISWAP_EXTENSION_RDNS);
  const extensionRequest = useCallback(
    async (method, arg) => {
      const provider = await connector?.getProvider();
      if (!provider.request) {
        return;
      }
      await provider.request({
        method,
        params: [arg]
      });
    },
    [connector]
  );
  return useMemo(() => {
    return connector ? { ...connector, extensionRequest } : void 0;
  }, [connector, extensionRequest]);
}
